import { memo, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Divider, Heading, Icon, Text } from '@zillow/constellation-10';

import { getConstellationIcon } from '../../../../../utils/iconUtils';

import { getRecordFromURLSearchParams } from '../utils';

import { FEE_SOURCE_KEYS, FILTER_KEYS_MAP, FILTER_KEYS_VALUES_ARRAY } from '../constants';

import { FeeListItem } from './FeeListItem';

import type {
  FeeCategoryDictionaryItemDto,
  GetMultifamilyFeesDictionaryItemsDto,
  MultifamilyFeeDto,
} from '@zg-rentals/ts-rental-manager-api-beta-client';

const getFeeCategoryDisplay = (
  feeCategoryKey: string,
  dictionaryItems: Record<string, FeeCategoryDictionaryItemDto>,
) => {
  const { displayName, feeCategoryIconName } = dictionaryItems[feeCategoryKey];
  const IconComponent = getConstellationIcon({ iconName: feeCategoryIconName });
  return (
    <Box css={{ padding: '400', display: 'flex', flexDirection: 'row', gap: '400' }}>
      {IconComponent && <Icon size="md" render={<IconComponent />} />}
      <Heading level={5}>{displayName}</Heading>
    </Box>
  );
};

const renderFeeCategory = ({
  categoryKey,
  selectedFilters,
  feeListItemsCountRef,
  dictionaryItems,
  existingFees,
  handleEditFeeClick,
}: {
  categoryKey: string;
  selectedFilters: SelectedFiltersProps;
  feeListItemsCountRef: { value: number };
} & Props) => {
  const feeListItems = existingFees[categoryKey]
    .map((fee) => {
      if (
        selectedFilters[FILTER_KEYS_MAP.FREQUENCY] &&
        !selectedFilters[FILTER_KEYS_MAP.FREQUENCY][fee.feePaymentFrequencyCategoryType]
      ) {
        return null;
      }

      if (
        selectedFilters[FILTER_KEYS_MAP.REQUIREMENT] &&
        !selectedFilters[FILTER_KEYS_MAP.REQUIREMENT][fee.feeRequirementType]
      ) {
        return null;
      }

      if (
        selectedFilters[FILTER_KEYS_MAP.FEE_LEVEL] &&
        !selectedFilters[FILTER_KEYS_MAP.FEE_LEVEL][fee.feeApplicableLevelType] &&
        !fee.unit.some((unitId) => selectedFilters[FILTER_KEYS_MAP.FEE_LEVEL][unitId]) &&
        !fee.floorplan.some((floorplanId) => selectedFilters[FILTER_KEYS_MAP.FEE_LEVEL][floorplanId])
      ) {
        return null;
      }

      if (
        selectedFilters[FILTER_KEYS_MAP.FEE_SOURCE] &&
        !(fee.isImportedFromFeed && selectedFilters[FILTER_KEYS_MAP.FEE_SOURCE][FEE_SOURCE_KEYS.FEED]) &&
        !(!fee.isImportedFromFeed && selectedFilters[FILTER_KEYS_MAP.FEE_SOURCE][FEE_SOURCE_KEYS.MANUALLY])
      ) {
        return null;
      }

      return (
        <div key={fee.feeGroupId}>
          <FeeListItem fee={fee} dictionaryItems={dictionaryItems} handleEditFeeClick={handleEditFeeClick} />
        </div>
      );
    })
    .filter((item) => item);

  if (!feeListItems.length) {
    return null;
  }

  feeListItemsCountRef.value = feeListItemsCountRef.value + feeListItems.length;

  return (
    <Box key={categoryKey} css={{ marginBottom: '800' }}>
      {getFeeCategoryDisplay(categoryKey, dictionaryItems.feeCategory)}

      <Divider />

      {feeListItems}
    </Box>
  );
};

type Props = {
  dictionaryItems: GetMultifamilyFeesDictionaryItemsDto;
  existingFees: Record<string, Array<MultifamilyFeeDto>>;
  handleEditFeeClick: (fee: MultifamilyFeeDto) => void;
};

type SelectedFiltersProps = Record<string, Record<string, boolean>>;

const FeesByCategoryToBeMemo = ({ dictionaryItems, existingFees, handleEditFeeClick }: Props) => {
  const router = useRouter();

  const urlSearchParams = useMemo(() => new URLSearchParams(router.asPath.split('?')[1] || ''), [router.asPath]);

  const [selectedFilters, setSelectedFilters] = useState<SelectedFiltersProps>({});

  useEffect(() => {
    const selectedFiltersToBe: SelectedFiltersProps = {};

    for (const keyName of FILTER_KEYS_VALUES_ARRAY) {
      if (urlSearchParams.has(keyName)) {
        selectedFiltersToBe[keyName] = getRecordFromURLSearchParams({ urlSearchParams, keyName });
      } else if (selectedFiltersToBe[keyName]) {
        delete selectedFiltersToBe[keyName];
      }
    }

    setSelectedFilters(selectedFiltersToBe);
  }, [urlSearchParams]);

  const renderFeesByCategory = () => {
    const feeListItemsCountRef = { value: 0 };
    const renderFeeCategoryGenericArgs = {
      categoryKey: '',
      selectedFilters,
      feeListItemsCountRef,
      dictionaryItems,
      existingFees,
      handleEditFeeClick,
    };

    const feesByCategoryList = Object.keys(existingFees).map((categoryKey) => {
      renderFeeCategoryGenericArgs.categoryKey = categoryKey;

      if (selectedFilters[FILTER_KEYS_MAP.CATEGORY]) {
        return selectedFilters[FILTER_KEYS_MAP.CATEGORY][categoryKey]
          ? renderFeeCategory(renderFeeCategoryGenericArgs)
          : null;
      }

      return renderFeeCategory(renderFeeCategoryGenericArgs);
    });

    if (feeListItemsCountRef.value <= 0) {
      return <Text>No fees match your current filters.</Text>;
    }

    return feesByCategoryList;
  };

  return <div data-testid="FeesByCategory">{renderFeesByCategory()}</div>;
};

export const FeesByCategory = memo(FeesByCategoryToBeMemo);
